import { useEffect, useState } from 'react'

import useGeoLocation from '~/hooks/useLocation'
import type { CollectionChartData } from '~/types/collectionChart'

export enum FilterType {
  all = 'all',
  tenYear = 'tenYear',
  fiveYear = 'fiveYear',
  twelveMonths = 'twelveMonths',
}

interface UseCollectionChartOptions {
  collectionId: string
  initialData: CollectionChartData | null
}

const useCollectionChart = ({
  collectionId,
  initialData,
}: UseCollectionChartOptions) => {
  const { activeCurrencyIso } = useGeoLocation()
  const [chartData, setChartData] = useState<CollectionChartData | null>(
    initialData
  )
  const [currentFilter, setCurrentFilter] = useState<FilterType>(
    FilterType.tenYear
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const fetchChartData = async (filter: FilterType) => {
    setLoading(true)
    setError(null)

    try {
      const queryParams = new URLSearchParams()

      if (activeCurrencyIso) {
        queryParams.append('currency', activeCurrencyIso.toUpperCase())
      }

      const currentYear = new Date().getFullYear()

      switch (filter) {
        case FilterType.twelveMonths:
          queryParams.append('period', '3month')
          queryParams.append('min_year', String(currentYear - 1))
          break
        case FilterType.fiveYear:
          queryParams.append('period', 'yearly')
          queryParams.append('min_year', String(currentYear - 5))
          break
        case FilterType.tenYear:
          queryParams.append('period', 'yearly')
          queryParams.append('min_year', String(currentYear - 10))
          break
        case FilterType.all:
        default:
          queryParams.append('period', 'yearly')
          break
      }

      const url = `/api/collection-market-performance/${collectionId}?${queryParams.toString()}`
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`)
      }

      const data: CollectionChartData = await response.json()
      setChartData(data)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch chart data'
      setError(errorMessage)
      console.error('Error fetching collection chart data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!collectionId) return
    if (!activeCurrencyIso) return

    fetchChartData(currentFilter)
  }, [currentFilter, collectionId, activeCurrencyIso])

  return {
    chartData,
    currentFilter,
    setCurrentFilter,
    loading,
    error,
  }
}

export default useCollectionChart
